import { Component, Inject, DOCUMENT } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faUserCircle,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { SharedUserService } from '../../shared/services/shared/user/shared-user.service';
import {
  NgbAccordionModule,
  NgbDropdownModule,
} from '@ng-bootstrap/ng-bootstrap';
import { NavbarControlComponent } from '../../shared/controls/navbar-control/navbar-control.component';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Meta, Title } from '@angular/platform-browser';

@Component({
  selector: 'app-documentation',
  imports: [
    RouterModule,
    FontAwesomeModule,
    NgbDropdownModule,
    NgbAccordionModule,
    NavbarControlComponent,
  ],
  templateUrl: './documentation.component.html',
  styleUrl: './documentation.component.scss',
})
export class DocumentationComponent {
  public document: IDocument = DocumentData;
  public isLoggedIn: boolean = false;
  public faUserCircle: IconDefinition = faUserCircle;

  constructor(
    private _sharedUserService: SharedUserService,
    private _translateService: TranslateService,
    private _http: HttpClient,
    @Inject(DOCUMENT) private doc: Document,
    private _router: Router,
    private _titleService: Title,
    private metaService: Meta,
  ) {}

  public ngOnInit(): void {
    let lang = this._translateService.currentLang || 'en';

    this._http
      .get<any>('./assets/i18n/static-content/documentation.json')
      .subscribe({
        next: (res) => {
          this.document = res[lang];
        },
      });
    // ✅ Page Title (search + intent focused)
    this._titleService.setTitle(
      'ClockFlex Documentation | Setup, Guides & API References',
    );

    // ✅ Canonical URL
    const canonicalUrl = `https://clockflex.com${this._router.url.split('?')[0]}`;
    this.setCanonicalUrl(canonicalUrl);

    // ✅ Meta Description
    this.metaService.updateTag({
      name: 'description',
      content:
        'Explore ClockFlex documentation to learn how to set up scheduling, manage bookings, configure availability, and integrate ClockFlex into your workflow.',
    });

    // ❌ DO NOT add meta robots (handled by server)

    // ✅ Open Graph
    this.metaService.updateTag({
      property: 'og:title',
      content: 'ClockFlex Documentation | Setup, Guides & API References',
    });

    this.metaService.updateTag({
      property: 'og:description',
      content:
        'Learn how to use ClockFlex with step-by-step guides, setup instructions, and documentation for managing scheduling and bookings.',
    });

    this.metaService.updateTag({
      property: 'og:url',
      content: canonicalUrl,
    });

    this.metaService.updateTag({
      property: 'og:type',
      content: 'website',
    });

    this.metaService.updateTag({
      property: 'og:site_name',
      content: 'ClockFlex',
    });

    // ✅ Twitter
    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.metaService.updateTag({
      name: 'twitter:title',
      content: 'ClockFlex Documentation | Setup, Guides & API References',
    });

    this.metaService.updateTag({
      name: 'twitter:description',
      content:
        'Official ClockFlex documentation to help you get started and master scheduling, bookings, and availability management.',
    });

    this.metaService.updateTag({
      name: 'twitter:image',
      content: 'https://clockflex.com/assets/images/og-image.png',
    });

    this.subscribeUserService();
  }

  private setCanonicalUrl(url: string): void {
    if (!url.startsWith('http')) {
      return;
    }

    let link: HTMLLinkElement | null = this.doc.querySelector(
      "link[rel='canonical']",
    );

    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  private subscribeUserService(): void {
    this._sharedUserService.loggedIn$.subscribe((response) => {
      this.isLoggedIn = response ? true : false;
    });
  }

  public replace(text: string): string {
    return text;
  }
}

interface IDocument {
  topics: ITopic[];
}

interface ITopic {
  id: number;
  title: string;
  subtopics: ISubtopic[];
  description: string;
}

interface ISubtopic {
  id: number;
  title: string;
  description: string;
}

export const DocumentData: IDocument = {
  topics: [
    {
      id: 1,
      title: 'User Accounts & Login',
      subtopics: [
        {
          id: 1,
          title: 'Create Account & Login',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">Creating an Account on ClockFlex</p><p style="font-size: 16px; font-weight: bold; margin-top: 20px;">Steps to Create an Account</p><ol style="margin-top: 5px; padding-left: 20px;"><li>Go to the <strong>ClockFlex website</strong>.</li><li>Click <strong>Sign Up</strong>.</li><li>Enter your <strong>email, password, and name</strong>.</li><li>Click <strong>Create Account</strong>.</li><li>After logging in, verify your email.</li></ol><p style="font-size: 16px; font-weight: bold; margin-top: 20px;">Login Process</p><ol style="margin-top: 5px; padding-left: 20px;"><li>Go to the <strong>ClockFlex Login Page</strong>.</li><li>Enter your registered <strong>email and password</strong>.</li><li>Click <strong>Login</strong>.</li></ol></div>',
        },
      ],
      description:
        'Guidelines for creating an account, logging in, verifying email after login, resetting passwords, and logging out of ClockFlex.',
    },
    {
      id: 2,
      title: 'Organizations',
      subtopics: [
        {
          id: 1,
          title: 'Create an Organization',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">Creating an Organization in ClockFlex</p><ol style="margin-top: 5px; padding-left: 20px;"><li>After logging in, you will land on the <strong>Organizations Page</strong>.</li><li>Click <strong>Create Organization</strong>.</li><li>Enter your organization’s <strong>name</strong> and other required details.</li><li>Click <strong>Create</strong> to finish setup.</li><li>You will be redirected to your new organization’s dashboard.</li></ol></div>',
        },
      ],
      description: 'Instructions on creating an organization in ClockFlex.',
    },
    {
      id: 3,
      title: 'Appointment Types',
      subtopics: [
        {
          id: 1,
          title: 'Create Appointment Type',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">Creating an Appointment Type in ClockFlex</p><ol style="margin-top: 5px; padding-left: 20px;"><li>Go to your <strong>Organization Dashboard</strong>.</li><li>Click on <strong>Appointment Types</strong>.</li><li>Click <strong>Create New</strong>.</li><li>Enter the <strong>name, duration, and description</strong> of the appointment type.</li><li>Choose if this appointment type should be <strong>available for booking</strong>.</li><li>Set pricing (if applicable).</li><li>Click <strong>Save</strong> to finish.</li></ol></div>',
        },
      ],
      description:
        'Learn how to create different types of appointments in ClockFlex.',
    },
    {
      id: 4,
      title: 'Availability',
      subtopics: [
        {
          id: 1,
          title: 'Set Up Availability',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">Managing Calendar for Resource-Based Scheduling</p><ol style="margin-top: 5px; padding-left: 20px;"><li>Go to your <strong>Organization Dashboard</strong>.</li><li>Add or manage availability for specific calendar.</li><li>Define booking slots based on resource availability.</li><li>Add/Remove appointment types for the calendar by clicking on \'Edit Group\'.</li><li>Click <strong>Save</strong> to confirm changes.</li></ol></div>',
        },
      ],
      description:
        'Learn how to configure and manage your availability, including calendar-based resource scheduling, in ClockFlex.',
    },
    {
      id: 5,
      title: 'Appointments',
      subtopics: [
        {
          id: 1,
          title: 'Add Appointment',
          description: `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
            <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">Adding a New Appointment</p>
            <ol style="margin-top: 5px; padding-left: 20px;">
              <li>After logging in, go to <strong>Organization</strong>.</li>
              <li>Click on <strong>Calendar</strong> in the side navigation.</li>
              <li>Choose an <strong>Appointment Type</strong> from the list.</li>
              <li>Select the desired date and time slot.</li>
              <li>Select the desired calendar.</li>
              <li>Enter client details (name, email, phone number, etc.).</li>
              <li>Click <strong>Confirm</strong> to finalize the appointment.</li>
              <li>A confirmation email will be sent to the client.</li>
            </ol>
          </div>`,
        },
        {
          id: 2,
          title: 'Reschedule Appointment',
          description: `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
            <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">Rescheduling an Appointment</p>
            <ol style="margin-top: 5px; padding-left: 20px;">
              <li>Navigate to <strong>Organization</strong>.</li>
              <li>Click on <strong>Calendar</strong> in the side navigation.</li>
              <li>Find the appointment you want to reschedule.</li>
              <li>Click <strong>Reschedule</strong> and select a new date/time.</li>
              <li>Confirm the changes.</li>
              <li>The client will receive a notification about the update.</li>
            </ol>
          </div>`,
        },
        {
          id: 3,
          title: 'Cancel Appointment',
          description: `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
            <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">Canceling an Appointment</p>
            <ol style="margin-top: 5px; padding-left: 20px;">
              <li>Go to <strong>Organization</strong>.</li>
              <li>Click on <strong>Calendar</strong> in the side navigation.</li>
              <li>Locate the appointment to cancel.</li>
              <li>Click <strong>Cancel</strong> and provide a reason (optional).</li>
              <li>Confirm the cancellation.</li>
              <li>The client will receive a cancellation email.</li>
            </ol>
          </div>`,
        },
      ],
      description:
        'Manage appointments in ClockFlex, including adding, rescheduling, and canceling bookings.',
    },
    {
      id: 6,
      title: 'Online Workspaces',
      subtopics: [
        {
          id: 1,
          title: 'Zoom Workplace',
          description: `<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
          <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">
            How to Connect, Use, and Remove Zoom with ClockFlex
          </p>
        
          <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">Requirements</p>
          <ul style="margin-top: 5px; padding-left: 20px;">
            <li>A <strong>Zoom account</strong> is required.</li>
            <li>You must have <strong>Administrator permissions</strong> in ClockFlex.</li>
          </ul>
        
          <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">Key Rules</p>
          <ul style="margin-top: 5px; padding-left: 20px;">
            <li><strong>ClockFlex can only connect to one Zoom account at a time.</strong></li>
            <li><strong>Each Zoom account can only connect to one ClockFlex instance.</strong></li>
          </ul>
        
          <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">Connecting Zoom to ClockFlex</p>
          <ol style="margin-top: 5px; padding-left: 20px;">
            <li>Select <strong>organization</strong> in ClockFlex.</li>
            <li>Navigate to the <strong>Integrations panel</strong> from left panel</li>
            <li>Locate <strong>Zoom</strong> and click <strong>on it</strong>.</li>
            <li>Click <strong>Connect to Zoom</strong>.</li>
            <li>Log into Zoom using your credentials or via <strong>SSO, Google, or Facebook</strong>.</li>
            <li>Click <strong>Pre-approve</strong>, then click <strong>Authorize</strong>.</li>
            <li>Select the <strong>appointment types</strong> you want Zoom to work with.</li>
            <li>Customize your <strong>Zoom meeting settings</strong> as needed.</li>
            <li>Click <strong>Save</strong>.</li>
          </ol>
        
          <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">Using Zoom for Appointments</p>
        
          <p style="margin-top: 10px; font-weight: bold;">Appointment Creation</p>
          <ul style="margin-top: 5px; padding-left: 20px;">
            <li>When an appointment is <strong>booked</strong>, ClockFlex will <strong>automatically schedule a Zoom meeting</strong> if Zoom is enabled for that appointment type.</li>
            <li>The Zoom link will be included in:
              <ul style="padding-left: 20px;">
                <li>The <strong>confirmation email</strong> sent to the client.</li>
                <li>The <strong>appointment details</strong> in ClockFlex.</li>
              </ul>
            </li>
          </ul>
        
          <p style="margin-top: 10px; font-weight: bold;">Appointment Rescheduling</p>
          <ul style="margin-top: 5px; padding-left: 20px;">
            <li>If an appointment is <strong>rescheduled</strong>, the <strong>existing Zoom meeting will be updated</strong> to reflect the new date and time.</li>
            <li>The updated Zoom link (if changed) will be sent via email and updated in ClockFlex.</li>
          </ul>
        
          <p style="margin-top: 10px; font-weight: bold;">Appointment Cancellation</p>
          <ul style="margin-top: 5px; padding-left: 20px;">
            <li>If an appointment is <strong>canceled</strong>, the <strong>associated Zoom meeting will be automatically deleted</strong>.</li>
            <li>The cancellation notification will be sent to the client, and the Zoom link will no longer be valid.</li>
          </ul>
        
          <p style="margin-top: 10px; font-weight: bold;">Starting a Zoom Meeting</p>
          <ol style="margin-top: 5px; padding-left: 20px;">
            <li>Open the <strong>appointment details</strong> in ClockFlex.</li>
            <li>Click on the <strong>Zoom meeting link</strong> to join the session.</li>
            <li>You will be redirected to Zoom, where you can start the meeting.</li>
          </ol>
        
          <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">Removing Zoom from ClockFlex</p>
        
          <p style="margin-top: 10px; font-weight: bold;">Step 1: Remove Zoom from ClockFlex</p>
          <ol style="margin-top: 5px; padding-left: 20px;">
            <li>Navigate to the <strong>Integrations panel</strong> in ClockFlex.</li>
            <li>Locate <strong>Zoom</strong> and click <strong>Edit</strong>.</li>
            <li>Click <strong>Disconnect Zoom</strong> to unlink the account.</li>
          </ol>
        
          <p style="margin-top: 10px; font-weight: bold;">Step 2: Remove ClockFlex from Zoom</p>
          <ol style="margin-top: 5px; padding-left: 20px;">
            <li><strong>Log into Zoom</strong> at <a href="https://marketplace.zoom.us/" target="_blank">Zoom App Marketplace</a>.</li>
            <li>Click on <strong>Manage</strong> in the top-right corner.</li>
            <li>Go to <strong>Installed Apps</strong> and find <strong>ClockFlex</strong>.</li>
            <li>Click <strong>Uninstall</strong> and confirm the removal.</li>
          </ol>
        
          <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">Important Notes</p>
          <ul style="margin-top: 5px; padding-left: 20px;">
            <li><strong>Default Behavior:</strong> New appointment types will have video conferencing <strong>disabled</strong> by default.</li>
            <li>To enable video conferencing for a new appointment type:
              <ol style="padding-left: 20px;">
                <li>Go to the <strong>Integrations panel</strong>.</li>
                <li>Locate <strong>Zoom</strong> and click <strong>Edit</strong>.</li>
              </ol>
            </li>
          </ul>
        </div>
        `,
        },
      ],
      description: 'Instructions on integrating Zoom with ClockFlex.',
    },
  ],
};

export const DocumentDataGu: IDocument = {
  topics: [
    {
      id: 1,
      title: 'વપરાશકર્તા ખાતાઓ અને લોગિન',
      subtopics: [
        {
          id: 1,
          title: 'ખાતું બનાવો અને લોગિન કરો',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">ક્લોકફ્લેક્સ પર ખાતું બનાવવું</p><p style="font-size: 16px; font-weight: bold; margin-top: 20px;">ખાતું બનાવવા માટેના પગલાં</p><ol style="margin-top: 5px; padding-left: 20px;"><li><strong>ક્લોકફ્લેક્સ વેબસાઇટ</strong> પર જાઓ.</li><li><strong>સાઇન અપ</strong> પર ક્લિક કરો.</li><li>તમારું <strong>ઇમેઇલ, પાસવર્ડ અને નામ</strong> દાખલ કરો.</li><li><strong>ખાતું બનાવો</strong> પર ક્લિક કરો.</li><li>લોગ ઇન કર્યા પછી, તમારું ઇમેઇલ ચકાસો.</li></ol><p style="font-size: 16px; font-weight: bold; margin-top: 20px;">લોગિન પ્રક્રિયા</p><ol style="margin-top: 5px; padding-left: 20px;"><li><strong>ક્લોકફ્લેક્સ લોગિન પેજ</strong> પર જાઓ.</li><li>તમારું રજિસ્ટર્ડ <strong>ઇમેઇલ અને પાસવર્ડ</strong> દાખલ કરો.</li><li><strong>લોગિન</strong> પર ક્લિક કરો.</li></ol></div>',
        },
      ],
      description:
        'ક્લોકફ્લેક્સમાં ખાતું બનાવવા, લોગ ઇન કરવા, લોગ ઇન કર્યા પછી ઇમેઇલ ચકાસવા, પાસવર્ડ ફરીથી સેટ કરવા અને લોગ આઉટ કરવા માટેની માર્ગદર્શિકા.',
    },
    {
      id: 2,
      title: 'સંસ્થાઓ',
      subtopics: [
        {
          id: 1,
          title: 'સંસ્થા બનાવો',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">ક્લોકફ્લેક્સમાં સંસ્થા બનાવવી</p><ol style="margin-top: 5px; padding-left: 20px;"><li>લોગ ઇન કર્યા પછી, તમે <strong>સંસ્થાઓ પેજ</strong> પર પહોંચશો.</li><li><strong>સંસ્થા બનાવો</strong> પર ક્લિક કરો.</li><li>તમારી સંસ્થાનું <strong>નામ</strong> અને અન્ય જરૂરી વિગતો દાખલ કરો.</li><li>સેટઅપ પૂર્ણ કરવા માટે <strong>બનાવો</strong> પર ક્લિક કરો.</li><li>તમને તમારી નવી સંસ્થાના ડેશબોર્ડ પર રીડાયરેક્ટ કરવામાં આવશે.</li></ol></div>',
        },
      ],
      description: 'ક્લોકફ્લેક્સમાં સંસ્થા કેવી રીતે બનાવવી તેની સૂચનાઓ.',
    },
    {
      id: 3,
      title: 'એપોઇન્ટમેન્ટ પ્રકારો',
      subtopics: [
        {
          id: 1,
          title: 'એપોઇન્ટમેન્ટ પ્રકાર બનાવો',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">ક્લોકફ્લેક્સમાં એપોઇન્ટમેન્ટ પ્રકાર બનાવવો</p><ol style="margin-top: 5px; padding-left: 20px;"><li>તમારા <strong>સંસ્થા ડેશબોર્ડ</strong> પર જાઓ.</li><li><strong>એપોઇન્ટમેન્ટ પ્રકારો</strong> પર ક્લિક કરો.</li><li><strong>નવું બનાવો</strong> પર ક્લિક કરો.</li><li>એપોઇન્ટમેન્ટ પ્રકારનું <strong>નામ, સમયગાળો અને વર્ણન</strong> દાખલ કરો.</li><li>પસંદ કરો કે શું આ એપોઇન્ટમેન્ટ પ્રકાર <strong>બુકિંગ માટે ઉપલબ્ધ</strong> હોવો જોઈએ.</li><li>કિંમત સેટ કરો (જો લાગુ હોય તો).</li><li>પૂર્ણ કરવા માટે <strong>સાચવો</strong> પર ક્લિક કરો.</li></ol></div>',
        },
      ],
      description:
        'ક્લોકફ્લેક્સમાં વિવિધ પ્રકારની એપોઇન્ટમેન્ટ કેવી રીતે બનાવવી તે જાણો.',
    },
    {
      id: 4,
      title: 'ઉપલબ્ધતા',
      subtopics: [
        {
          id: 1,
          title: 'ઉપલબ્ધતા સેટ કરો',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">સંસાધન-આધારિત શેડ્યુલિંગ માટે કૅલેન્ડરનું સંચાલન</p><ol style="margin-top: 5px; padding-left: 20px;"><li>તમારા <strong>સંસ્થા ડેશબોર્ડ</strong> પર જાઓ.</li><li>ચોક્કસ કૅલેન્ડર માટે ઉપલબ્ધતા ઉમેરો અથવા તેનું સંચાલન કરો.</li><li>સંસાધનની ઉપલબ્ધતાના આધારે બુકિંગ સ્લોટ વ્યાખ્યાયિત કરો.</li><li>\'ગ્રુપ સંપાદિત કરો\' પર ક્લિક કરીને કૅલેન્ડર માટે એપોઇન્ટમેન્ટ પ્રકારો ઉમેરો/દૂર કરો.</li><li>ફેરફારોની પુષ્ટિ કરવા માટે <strong>સાચવો</strong> પર ક્લિક કરો.</li></ol></div>',
        },
      ],
      description:
        'ક્લોકફ્લેક્સમાં કૅલેન્ડર-આધારિત સંસાધન શેડ્યુલિંગ સહિત તમારી ઉપલબ્ધતાને કેવી રીતે ગોઠવવી અને તેનું સંચાલન કરવું તે જાણો.',
    },
    {
      id: 5,
      title: 'એપોઇન્ટમેન્ટ્સ',
      subtopics: [
        {
          id: 1,
          title: 'એપોઇન્ટમેન્ટ ઉમેરો',
          description: `
            <div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
              <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">નવી એપોઇન્ટમેન્ટ ઉમેરી રહ્યા છીએ</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li>લોગ ઇન કર્યા પછી, <strong>સંસ્થા</strong> પર જાઓ.</li>
                <li>સાઇડ નેવિગેશનમાં <strong>કેલેન્ડર</strong> પર ક્લિક કરો.</li>
                <li>સૂચિમાંથી <strong>એપોઇન્ટમેન્ટનો પ્રકાર</strong> પસંદ કરો.</li>
                <li>ઇચ્છિત તારીખ અને સમય સ્લોટ પસંદ કરો.</li>
                <li>ઇચ્છિત કેલેન્ડર પસંદ કરો.</li>
                <li>ક્લાયન્ટની વિગતો (નામ, ઇમેઇલ, ફોન નંબર વગેરે) દાખલ કરો.</li>
                <li>એપોઇન્ટમેન્ટને અંતિમ સ્વરૂપ આપવા માટે <strong>કન્ફર્મ</strong> પર ક્લિક કરો.</li>
                <li>ક્લાયન્ટને પુષ્ટિકરણ ઇમેઇલ મોકલવામાં આવશે.</li>
              </ol>
            </div>`,
        },
        {
          id: 2,
          title: 'એપોઇન્ટમેન્ટ ફરીથી શેડ્યૂલ કરો',
          description: `
            <div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
              <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">એપોઇન્ટમેન્ટ ફરીથી શેડ્યૂલ કરી રહ્યા છીએ</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li><strong>સંસ્થા</strong> પર નેવિગેટ કરો.</li>
                <li>સાઇડ નેવિગેશનમાં <strong>કેલેન્ડર</strong> પર ક્લિક કરો.</li>
                <li>તમે જે એપોઇન્ટમેન્ટ ફરીથી શેડ્યૂલ કરવા માંગો છો તે શોધો.</li>
                <li><strong>ફરીથી શેડ્યૂલ કરો</strong> પર ક્લિક કરો અને નવી તારીખ/સમય પસંદ કરો.</li>
                <li>ફેરફારોની પુષ્ટિ કરો.</li>
                <li>ક્લાયન્ટને અપડેટ વિશે સૂચના પ્રાપ્ત થશે.</li>
              </ol>
            </div>`,
        },
        {
          id: 3,
          title: 'એપોઇન્ટમેન્ટ રદ કરો',
          description: `
            <div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
              <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">એપોઇન્ટમેન્ટ રદ કરી રહ્યા છીએ</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li><strong>સંસ્થા</strong> પર જાઓ.</li>
                <li>સાઇડ નેવિગેશનમાં <strong>કેલેન્ડર</strong> પર ક્લિક કરો.</li>
                <li>રદ કરવા માટેની એપોઇન્ટમેન્ટ શોધો.</li>
                <li><strong>રદ કરો</strong> પર ક્લિક કરો અને કારણ આપો (વૈકલ્પિક).</li>
                <li>રદ કરવાની પુષ્ટિ કરો.</li>
                <li>ક્લાયન્ટને રદ કરવાની ઇમેઇલ પ્રાપ્ત થશે.</li>
              </ol>
            </div>`,
        },
      ],
      description:
        'ક્લોકફ્લેક્સમાં એપોઇન્ટમેન્ટનું સંચાલન કરો, જેમાં બુકિંગ ઉમેરવા, ફરીથી શેડ્યૂલ કરવા અને રદ કરવાનો સમાવેશ થાય છે.',
    },
    {
      id: 6,
      title: 'ઓનલાઇન વર્કસ્પેસ',
      subtopics: [
        {
          id: 1,
          title: 'ઝૂમ વર્કપ્લેસ',
          description: `<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
              <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">
                ક્લોકફ્લેક્સ સાથે ઝૂમને કેવી રીતે કનેક્ટ કરવું, ઉપયોગ કરવો અને દૂર કરવું
              </p>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">જરૂરિયાતો</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li><strong>ઝૂમ એકાઉન્ટ</strong> હોવું જરૂરી છે.</li>
                <li>તમારી પાસે ક્લોકફ્લેક્સમાં <strong>એડમિનિસ્ટ્રેટર પરવાનગીઓ</strong> હોવી આવશ્યક છે.</li>
              </ul>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">મુખ્ય નિયમો</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li><strong>ક્લોકફ્લેક્સ એક સમયે ફક્ત એક જ ઝૂમ એકાઉન્ટ સાથે કનેક્ટ થઈ શકે છે.</strong></li>
                <li><strong>દરેક ઝૂમ એકાઉન્ટ ફક્ત એક જ ક્લોકફ્લેક્સ ઇન્સ્ટન્સ સાથે કનેક્ટ થઈ શકે છે.</strong></li>
              </ul>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">ક્લોકફ્લેક્સ સાથે ઝૂમ કનેક્ટ કરી રહ્યું છે</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li>ક્લોકફ્લેક્સમાં <strong>સંસ્થા</strong> પસંદ કરો.</li>
                <li>ડાબી પેનલમાંથી <strong>ઇન્ટિગ્રેશન્સ પેનલ</strong> પર નેવિગેટ કરો</li>
                <li><strong>ઝૂમ</strong> શોધો અને <strong>તેના પર</strong> ક્લિક કરો.</li>
                <li><strong>ઝૂમ સાથે કનેક્ટ કરો</strong> પર ક્લિક કરો.</li>
                <li>તમારા ઓળખપત્રોનો ઉપયોગ કરીને અથવા <strong>SSO, Google અથવા Facebook</strong> દ્વારા ઝૂમમાં લોગ ઇન કરો.</li>
                <li><strong>પ્રી-એપ્રુવ</strong> પર ક્લિક કરો, પછી <strong>ઓથોરાઇઝ</strong> પર ક્લિક કરો.</li>
                <li>તમે જે <strong>એપોઇન્ટમેન્ટ પ્રકારો</strong> સાથે ઝૂમને કામ કરવા માંગો છો તે પસંદ કરો.</li>
                <li>જરૂર મુજબ તમારી <strong>ઝૂમ મીટિંગ સેટિંગ્સ</strong> કસ્ટમાઇઝ કરો.</li>
                <li><strong>સાચવો</strong> પર ક્લિક કરો.</li>
              </ol>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">એપોઇન્ટમેન્ટ્સ માટે ઝૂમનો ઉપયોગ કરવો</p>
    
              <p style="margin-top: 10px; font-weight: bold;">એપોઇન્ટમેન્ટ બનાવવી</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li>જ્યારે કોઈ એપોઇન્ટમેન્ટ <strong>બુક</strong> કરવામાં આવે છે, ત્યારે જો તે એપોઇન્ટમેન્ટ પ્રકાર માટે ઝૂમ સક્ષમ કરેલું હોય તો ક્લોકફ્લેક્સ <strong>આપોઆપ ઝૂમ મીટિંગ શેડ્યૂલ કરશે</strong>.</li>
                <li>ઝૂમ લિંક આમાં શામેલ હશે:
                  <ul style="padding-left: 20px;">
                    <li>ક્લાયન્ટને મોકલવામાં આવેલ <strong>પુષ્ટિકરણ ઇમેઇલ</strong>.</li>
                    <li>ક્લોકફ્લેક્સમાં <strong>એપોઇન્ટમેન્ટની વિગતો</strong>.</li>
                  </ul>
                </li>
              </ul>
    
              <p style="margin-top: 10px; font-weight: bold;">એપોઇન્ટમેન્ટ ફરીથી શેડ્યૂલ કરવી</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li>જો કોઈ એપોઇન્ટમેન્ટ <strong>ફરીથી શેડ્યૂલ</strong> કરવામાં આવે છે, તો નવી તારીખ અને સમયને પ્રતિબિંબિત કરવા માટે <strong>હાલની ઝૂમ મીટિંગ અપડેટ કરવામાં આવશે</strong>.</li>
                <li>અપડેટ કરેલી ઝૂમ લિંક (જો બદલાઈ હોય તો) ઇમેઇલ દ્વારા મોકલવામાં આવશે અને ક્લોકફ્લેક્સમાં અપડેટ કરવામાં આવશે.</li>
              </ul>
    
              <p style="margin-top: 10px; font-weight: bold;">એપોઇન્ટમેન્ટ રદ કરવી</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li>જો કોઈ એપોઇન્ટમેન્ટ <strong>રદ</strong> કરવામાં આવે છે, તો તેની સાથે સંકળાયેલ <strong>ઝૂમ મીટિંગ આપોઆપ કાઢી નાખવામાં આવશે</strong>.</li>
                <li>રદ કરવાની સૂચના ક્લાયન્ટને મોકલવામાં આવશે, અને ઝૂમ લિંક હવે માન્ય રહેશે નહીં.</li>
              </ul>
    
              <p style="margin-top: 10px; font-weight: bold;">ઝૂમ મીટિંગ શરૂ કરવી</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li>ક્લોકફ્લેક્સમાં <strong>એપોઇન્ટમેન્ટની વિગતો</strong> ખોલો.</li>
                <li>સત્રમાં જોડાવા માટે <strong>ઝૂમ મીટિંગ લિંક</strong> પર ક્લિક કરો.</li>
                <li>તમને ઝૂમ પર રીડાયરેક્ટ કરવામાં આવશે, જ્યાં તમે મીટિંગ શરૂ કરી શકો છો.</li>
              </ol>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">ક્લોકફ્લેક્સમાંથી ઝૂમ દૂર કરી રહ્યા છીએ</p>
    
              <p style="margin-top: 10px; font-weight: bold;">પગલું 1: ક્લોકફ્લેક્સમાંથી ઝૂમ દૂર કરો</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li>ક્લોકફ્લેક્સમાં <strong>ઇન્ટિગ્રેશન્સ પેનલ</strong> પર નેવિગેટ કરો.</li>
                <li><strong>ઝૂમ</strong> શોધો અને <strong>સંપાદિત કરો</strong> પર ક્લિક કરો.</li>
                <li>એકાઉન્ટને અનલિંક કરવા માટે <strong>ઝૂમ ડિસ્કનેક્ટ કરો</strong> પર ક્લિક કરો.</li>
              </ol>
    
              <p style="margin-top: 10px; font-weight: bold;">પગલું 2: ઝૂમમાંથી ક્લોકફ્લેક્સ દૂર કરો</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li><a href="https://marketplace.zoom.us/" target="_blank">ઝૂમ એપ માર્કેટપ્લેસ</a> પર <strong>ઝૂમમાં લોગ ઇન કરો</strong>.</li>
                <li>ઉપર-જમણા ખૂણામાં <strong>મેનેજ કરો</strong> પર ક્લિક કરો.</li>
                <li><strong>ઇન્સ્ટોલ કરેલી એપ્સ</strong> પર જાઓ અને <strong>ક્લોકફ્લેક્સ</strong> શોધો.</li>
                <li><strong>અનઇન્સ્ટોલ કરો</strong> પર ક્લિક કરો અને દૂર કરવાની પુષ્ટિ કરો.</li>
              </ol>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">મહત્વપૂર્ણ નોંધો</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li><strong>ડિફૉલ્ટ વર્તન:</strong> નવા એપોઇન્ટમેન્ટ પ્રકારોમાં ડિફૉલ્ટ રૂપે વિડિયો કોન્ફરન્સિંગ <strong>અક્ષમ</strong> હશે.</li>
                <li>નવા એપોઇન્ટમેન્ટ પ્રકાર માટે વિડિયો કોન્ફરન્સિંગ સક્ષમ કરવા માટે:
                  <ol style="padding-left: 20px;">
                    <li><strong>ઇન્ટિગ્રેશન્સ પેનલ</strong> પર જાઓ.</li>
                    <li><strong>ઝૂમ</strong> શોધો અને <strong>સંપાદિત કરો</strong> પર ક્લિક કરો.</li>
                  </ol>
                </li>
              </ul>
            </div>
            `,
        },
      ],
      description: 'ક્લોકફ્લેક્સ સાથે ઝૂમને એકીકૃત કરવા અંગેની સૂચનાઓ.',
    },
  ],
};

export const DocumentDataHi: IDocument = {
  topics: [
    {
      id: 1,
      title: 'उपयोगकर्ता खाते और लॉग इन',
      subtopics: [
        {
          id: 1,
          title: 'खाता बनाएं और लॉग इन करें',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">क्लॉकफ्लेक्स पर खाता बनाना</p><p style="font-size: 16px; font-weight: bold; margin-top: 20px;">खाता बनाने के चरण</p><ol style="margin-top: 5px; padding-left: 20px;"><li><strong>क्लॉकफ्लेक्स वेबसाइट</strong> पर जाएं।</li><li><strong>साइन अप</strong> पर क्लिक करें।</li><li>अपना <strong>ईमेल, पासवर्ड और नाम</strong> दर्ज करें।</li><li><strong>खाता बनाएं</strong> पर क्लिक करें।</li><li>लॉग इन करने के बाद, अपना ईमेल सत्यापित करें।</li></ol><p style="font-size: 16px; font-weight: bold; margin-top: 20px;">लॉग इन प्रक्रिया</p><ol style="margin-top: 5px; padding-left: 20px;"><li><strong>क्लॉकफ्लेक्स लॉग इन पेज</strong> पर जाएं।</li><li>अपना पंजीकृत <strong>ईमेल और पासवर्ड</strong> दर्ज करें।</li><li><strong>लॉग इन</strong> पर क्लिक करें।</li></ol></div>',
        },
      ],
      description:
        'क्लॉकफ्लेक्स पर खाता बनाने, लॉग इन करने, लॉग इन के बाद ईमेल सत्यापित करने, पासवर्ड रीसेट करने और लॉग आउट करने के लिए दिशानिर्देश।',
    },
    {
      id: 2,
      title: 'संगठन',
      subtopics: [
        {
          id: 1,
          title: 'एक संगठन बनाएं',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">क्लॉकफ्लेक्स में एक संगठन बनाना</p><ol style="margin-top: 5px; padding-left: 20px;"><li>लॉग इन करने के बाद, आप <strong>संगठन पृष्ठ</strong> पर पहुंचेंगे।</li><li><strong>संगठन बनाएं</strong> पर क्लिक करें।</li><li>अपने संगठन का <strong>नाम</strong> और अन्य आवश्यक विवरण दर्ज करें।</li><li>सेटअप समाप्त करने के लिए <strong>बनाएं</strong> पर क्लिक करें।</li><li>आपको अपने नए संगठन के डैशबोर्ड पर पुनः निर्देशित किया जाएगा।</li></ol></div>',
        },
      ],
      description: 'क्लॉकफ्लेक्स में एक संगठन बनाने के निर्देश।',
    },
    {
      id: 3,
      title: 'अपॉइंटमेंट के प्रकार',
      subtopics: [
        {
          id: 1,
          title: 'अपॉइंटमेंट का प्रकार बनाएं',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">क्लॉकफ्लेक्स में अपॉइंटमेंट का प्रकार बनाना</p><ol style="margin-top: 5px; padding-left: 20px;"><li>अपने <strong>संगठन डैशबोर्ड</strong> पर जाएं।</li><li><strong>अपॉइंटमेंट के प्रकार</strong> पर क्लिक करें।</li><li><strong>नया बनाएं</strong> पर क्लिक करें।</li><li>अपॉइंटमेंट के प्रकार का <strong>नाम, अवधि और विवरण</strong> दर्ज करें।</li><li>चुनें कि क्या यह अपॉइंटमेंट प्रकार <strong>बुकिंग के लिए उपलब्ध</strong> होना चाहिए।</li><li>मूल्य निर्धारण सेट करें (यदि लागू हो)।</li><li>समाप्त करने के लिए <strong>सेव करें</strong> पर क्लिक करें।</li></ol></div>',
        },
      ],
      description: 'क्लॉकफ्लेक्स में विभिन्न प्रकार के अपॉइंटमेंट बनाना सीखें।',
    },
    {
      id: 4,
      title: 'उपलब्धता',
      subtopics: [
        {
          id: 1,
          title: 'उपलब्धता सेट करें',
          description:
            '<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;"><p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">संसाधन-आधारित शेड्यूलिंग के लिए कैलेंडर प्रबंधित करना</p><ol style="margin-top: 5px; padding-left: 20px;"><li>अपने <strong>संगठन डैशबोर्ड</strong> पर जाएं।</li><li>विशिष्ट कैलेंडर के लिए उपलब्धता जोड़ें या प्रबंधित करें।</li><li>संसाधन उपलब्धता के आधार पर बुकिंग स्लॉट परिभाषित करें।</li><li>\'समूह संपादित करें\' पर क्लिक करके कैलेंडर के लिए अपॉइंटमेंट प्रकार जोड़ें/हटाएं।</li><li>परिवर्तनों की पुष्टि करने के लिए <strong>सेव करें</strong> पर क्लिक करें।</li></ol></div>',
        },
      ],
      description:
        'क्लॉकफ्लेक्स में कैलेंडर-आधारित संसाधन शेड्यूलिंग सहित अपनी उपलब्धता को कॉन्फ़िगर और प्रबंधित करना सीखें।',
    },
    {
      id: 5,
      title: 'अपॉइंटमेंट',
      subtopics: [
        {
          id: 1,
          title: 'अपॉइंटमेंट जोड़ें',
          description: `
            <div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
              <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">एक नया अपॉइंटमेंट जोड़ना</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li>लॉग इन करने के बाद, <strong>संगठन</strong> पर जाएं।</li>
                <li>साइड नेविगेशन में <strong>कैलेंडर</strong> पर क्लिक करें।</li>
                <li>सूची से एक <strong>अपॉइंटमेंट का प्रकार</strong> चुनें।</li>
                <li>वांछित तिथि और समय स्लॉट का चयन करें।</li>
                <li>वांछित कैलेंडर का चयन करें।</li>
                <li>ग्राहक विवरण दर्ज करें (नाम, ईमेल, फ़ोन नंबर, आदि)।</li>
                <li>अपॉइंटमेंट को अंतिम रूप देने के लिए <strong>पुष्टि करें</strong> पर क्लिक करें।</li>
                <li>ग्राहक को एक पुष्टिकरण ईमेल भेजा जाएगा।</li>
              </ol>
            </div>`,
        },
        {
          id: 2,
          title: 'अपॉइंटमेंट पुनर्निर्धारित करें',
          description: `
            <div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
              <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">एक अपॉइंटमेंट को पुनर्निर्धारित करना</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li><strong>संगठन</strong> पर नेविगेट करें।</li>
                <li>साइड नेविगेशन में <strong>कैलेंडर</strong> पर क्लिक करें।</li>
                <li>वह अपॉइंटमेंट ढूंढें जिसे आप पुनर्निर्धारित करना चाहते हैं।</li>
                <li><strong>पुनर्निर्धारित करें</strong> पर क्लिक करें और एक नई तिथि/समय चुनें।</li>
                <li>परिवर्तनों की पुष्टि करें।</li>
                <li>ग्राहक को अपडेट के बारे में एक सूचना प्राप्त होगी।</li>
              </ol>
            </div>`,
        },
        {
          id: 3,
          title: 'अपॉइंटमेंट रद्द करें',
          description: `
            <div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
              <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">एक अपॉइंटमेंट रद्द करना</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li><strong>संगठन</strong> पर जाएं।</li>
                <li>साइड नेविगेशन में <strong>कैलेंडर</strong> पर क्लिक करें।</li>
                <li>रद्द करने के लिए अपॉइंटमेंट का पता लगाएं।</li>
                <li><strong>रद्द करें</strong> पर क्लिक करें और एक कारण बताएं (वैकल्पिक)।</li>
                <li>रद्दीकरण की पुष्टि करें।</li>
                <li>ग्राहक को एक रद्दीकरण ईमेल प्राप्त होगा।</li>
              </ol>
            </div>`,
        },
      ],
      description:
        'बुकिंग जोड़ने, पुनर्निर्धारित करने और रद्द करने सहित, क्लॉकफ्लेक्स में अपॉइंटमेंट प्रबंधित करें।',
    },
    {
      id: 6,
      title: 'ऑनलाइन कार्यस्थान',
      subtopics: [
        {
          id: 1,
          title: 'ज़ूम कार्यस्थान',
          description: `<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 700px;">
              <p style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">
                क्लॉकफ्लेक्स के साथ ज़ूम को कैसे कनेक्ट करें, उपयोग करें और हटाएं
              </p>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">आवश्यकताएँ</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li>एक <strong>ज़ूम खाता</strong> आवश्यक है।</li>
                <li>आपके पास क्लॉकफ्लेक्स में <strong>प्रशासक अनुमतियाँ</strong> होनी चाहिए।</li>
              </ul>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">मुख्य नियम</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li><strong>क्लॉकफ्लेक्स एक समय में केवल एक ज़ूम खाते से जुड़ सकता है।</strong></li>
                <li><strong>प्रत्येक ज़ूम खाता केवल एक क्लॉकफ्लेक्स इंस्टेंस से जुड़ सकता है।</strong></li>
              </ul>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">ज़ूम को क्लॉकफ्लेक्स से कनेक्ट करना</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li>क्लॉकफ्लेक्स में <strong>संगठन</strong> का चयन करें।</li>
                <li>बाएं पैनल से <strong>एकीकरण पैनल</strong> पर नेविगेट करें।</li>
                <li><strong>ज़ूम</strong> ढूंढें और <strong>उस पर</strong> क्लिक करें।</li>
                <li><strong>ज़ूम से कनेक्ट करें</strong> पर क्लिक करें।</li>
                <li>अपने क्रेडेंशियल का उपयोग करके या <strong>एसएसओ, गूगल या फेसबुक</strong> के माध्यम से ज़ूम में लॉग इन करें।</li>
                <li><strong>पूर्व-अनुमोदन करें</strong> पर क्लिक करें, फिर <strong>अधिकृत करें</strong> पर क्लिक करें।</li>
                <li>उन <strong>अपॉइंटमेंट प्रकारों</strong> का चयन करें जिनके साथ आप ज़ूम को काम करना चाहते हैं।</li>
                <li>आवश्यकतानुसार अपनी <strong>ज़ूम मीटिंग सेटिंग्स</strong> को अनुकूलित करें।</li>
                <li><strong>सेव करें</strong> पर क्लिक करें।</li>
              </ol>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">अपॉइंटमेंट के लिए ज़ूम का उपयोग करना</p>
    
              <p style="margin-top: 10px; font-weight: bold;">अपॉइंटमेंट निर्माण</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li>जब कोई अपॉइंटमेंट <strong>बुक</strong> किया जाता है, तो क्लॉकफ्लेक्स <strong>स्वचालित रूप से एक ज़ूम मीटिंग शेड्यूल करेगा</strong> यदि उस अपॉइंटमेंट प्रकार के लिए ज़ूम सक्षम है।</li>
                <li>ज़ूम लिंक इसमें शामिल किया जाएगा:
                  <ul style="padding-left: 20px;">
                    <li>ग्राहक को भेजे गए <strong>पुष्टिकरण ईमेल</strong>।</li>
                    <li>क्लॉकफ्लेक्स में <strong>अपॉइंटमेंट विवरण</strong>।</li>
                  </ul>
                </li>
              </ul>
    
              <p style="margin-top: 10px; font-weight: bold;">अपॉइंटमेंट पुनर्निर्धारण</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li>यदि कोई अपॉइंटमेंट <strong>पुनर्निर्धारित</strong> किया जाता है, तो नई तिथि और समय को दर्शाने के लिए <strong>मौजूदा ज़ूम मीटिंग अपडेट की जाएगी</strong>।</li>
                <li>अद्यतन किया गया ज़ूम लिंक (यदि बदला गया है) ईमेल के माध्यम से भेजा जाएगा और क्लॉकफ्लेक्स में अपडेट किया जाएगा।</li>
              </ul>
    
              <p style="margin-top: 10px; font-weight: bold;">अपॉइंटमेंट रद्द करना</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li>यदि कोई अपॉइंटमेंट <strong>रद्द</strong> किया जाता है, तो <strong>जुड़ी हुई ज़ूम मीटिंग स्वचालित रूप से हटा दी जाएगी</strong>।</li>
                <li>रद्दीकरण की सूचना ग्राहक को भेजी जाएगी, और ज़ूम लिंक अब मान्य नहीं होगा।</li>
              </ul>
    
              <p style="margin-top: 10px; font-weight: bold;">ज़ूम मीटिंग शुरू करना</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li>क्लॉकफ्लेक्स में <strong>अपॉइंटमेंट विवरण</strong> खोलें।</li>
                <li>सत्र में शामिल होने के लिए <strong>ज़ूम मीटिंग लिंक</strong> पर क्लिक करें।</li>
                <li>आपको ज़ूम पर पुनः निर्देशित किया जाएगा, जहाँ आप मीटिंग शुरू कर सकते हैं।</li>
              </ol>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">क्लॉकफ्लेक्स से ज़ूम हटाना</p>
    
              <p style="margin-top: 10px; font-weight: bold;">चरण 1: क्लॉकफ्लेक्स से ज़ूम हटाएं</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li>क्लॉकफ्लेक्स में <strong>एकीकरण पैनल</strong> पर नेविगेट करें।</li>
                <li><strong>ज़ूम</strong> ढूंढें और <strong>संपादित करें</strong> पर क्लिक करें।</li>
                <li>खाते को अनलिंक करने के लिए <strong>ज़ूम डिस्कनेक्ट करें</strong> पर क्लिक करें।</li>
              </ol>
    
              <p style="margin-top: 10px; font-weight: bold;">चरण 2: ज़ूम से क्लॉकफ्लेक्स हटाएं</p>
              <ol style="margin-top: 5px; padding-left: 20px;">
                <li><a href="https://marketplace.zoom.us/" target="_blank">ज़ूम ऐप मार्केटप्लेस</a> पर <strong>ज़ूम में लॉग इन करें</strong>।</li>
                <li>ऊपरी दाएं कोने में <strong>प्रबंधित करें</strong> पर क्लिक करें।</li>
                <li><strong>इंस्टॉल किए गए ऐप्स</strong> पर जाएं और <strong>क्लॉकफ्लेक्स</strong> ढूंढें।</li>
                <li><strong>अनइंस्टॉल करें</strong> पर क्लिक करें और हटाने की पुष्टि करें।</li>
              </ol>
    
              <p style="font-size: 16px; font-weight: bold; margin-top: 20px;">महत्वपूर्ण नोट्स</p>
              <ul style="margin-top: 5px; padding-left: 20px;">
                <li><strong>डिफ़ॉल्ट व्यवहार:</strong> नए अपॉइंटमेंट प्रकारों में डिफ़ॉल्ट रूप से वीडियो कॉन्फ्रेंसिंग <strong>अक्षम</strong> होगी।</li>
                <li>एक नए अपॉइंटमेंट प्रकार के लिए वीडियो कॉन्फ्रेंसिंग सक्षम करने के लिए:
                  <ol style="padding-left: 20px;">
                    <li><strong>एकीकरण पैनल</strong> पर जाएं।</li>
                    <li><strong>ज़ूम</strong> ढूंढें और <strong>संपादित करें</strong> पर क्लिक करें।</li>
                  </ol>
                </li>
              </ul>
            </div>
            `,
        },
      ],
      description: 'क्लॉकफ्लेक्स के साथ ज़ूम को एकीकृत करने के निर्देश।',
    },
  ],
};
